// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file Kbd.tsx
 * @input Uses React, StyleX, theme tokens
 * @output Exports Kbd component and KbdProps
 * @position Core implementation; renders styled keyboard shortcut indicators
 *
 * SYNC: When modified, update:
 * - /packages/core/src/Kbd/index.ts
 * - /packages/cli/templates/blocks/components/Kbd/ (showcase blocks)
 */
import React, { useSyncExternalStore } from 'react';
import * as stylex from '@stylexjs/stylex';
import { mergeProps } from "../utils/index.js";
import { themeProps } from "../utils/themeProps.js";
import "../theme/tokens.stylex.js";
import { colorVars, spacingVars, radiusVars, typographyVars, fontWeightVars, typeScaleVars } from "../theme/tokens.stylex.js";
import { jsx as _jsx } from "react/jsx-runtime";
const styles = {
  wrapper: {
    k1xSpc: "x3nfvp2",
    kGNEyG: "x6s0dn4",
    kOIVth: "xzye2dw",
    kmuXW: "x2lah0s",
    $$css: true
  }
};

/**
 * Map of modifier key names to display symbols.
 * Note: `mod` is not in this map — it resolves dynamically via platform
 * detection inside the component.
 */
const KEY_DISPLAY = {
  ctrl: '\u2303',
  // ⌃
  alt: '\u2325',
  // ⌥
  shift: '\u21E7',
  // ⇧
  enter: '\u21B5',
  // ↵
  backspace: '\u232B',
  // ⌫
  escape: 'Esc',
  tab: '\u21E5',
  // ⇥
  up: '\u2191',
  down: '\u2193',
  left: '\u2190',
  right: '\u2192',
  plus: '+'
};

/**
 * Resolves a key name to its display string. Handles the platform-aware
 * `mod` key (⌘ on macOS, Ctrl on other platforms) and falls back to
 * KEY_DISPLAY or uppercased key name.
 */
function getKeyDisplay(key, isMac) {
  if (key === 'mod') {
    return isMac ? '\u2318' : 'Ctrl';
  }
  return KEY_DISPLAY[key] ?? key.toUpperCase();
}

/**
 * Spoken-word labels for screen readers. The visual `KEY_DISPLAY` uses glyphs
 * (⌘, ⇧, ↵, …) that assistive tech cannot announce meaningfully, so the
 * accessible name for the shortcut is built from these words instead.
 */
const KEY_LABEL = {
  ctrl: 'Control',
  alt: 'Alt',
  shift: 'Shift',
  enter: 'Enter',
  backspace: 'Backspace',
  escape: 'Escape',
  tab: 'Tab',
  up: 'Up arrow',
  down: 'Down arrow',
  left: 'Left arrow',
  right: 'Right arrow',
  plus: 'Plus'
};
function getKeyLabel(key, isMac) {
  if (key === 'mod') {
    return isMac ? 'Command' : 'Control';
  }
  return KEY_LABEL[key] ?? key.toUpperCase();
}
function subscribeToPlatformChanges() {
  return () => {};
}
function getServerPlatformSnapshot() {
  return false;
}

/**
 * Detects whether the current platform is macOS/iOS.
 * Prefers the User-Agent Client Hints API when available (modern Chrome/Edge),
 * falls back to navigator.platform (deprecated but universally supported).
 */
function detectMac() {
  if (typeof navigator === 'undefined') {
    return false;
  }
  // Prefer User-Agent Client Hints API (not deprecated)
  const uaData = 'userAgentData' in navigator ? navigator.userAgentData : null;
  if (uaData && typeof uaData === 'object' && 'platform' in uaData) {
    return /mac/i.test(uaData.platform ?? '');
  }
  // Fallback: navigator.platform (deprecated but still shipped everywhere)
  return /Mac|iPhone|iPad|iPod/.test(navigator.platform ?? '');
}
/**
 * Displays a keyboard shortcut as styled <kbd> elements.
 *
 * A general-purpose component for rendering keyboard shortcuts
 * anywhere in the system — tooltips, menus, documentation, etc.
 *
 * Platform-aware: `mod` renders as ⌘ on macOS and Ctrl elsewhere.
 * SSR-safe — defers platform detection through useSyncExternalStore to avoid
 * hydration mismatches.
 *
 * @example
 * ```
 * <Kbd keys="mod+k" />
 * ```
 */
export function Kbd({
  keys,
  ref,
  xstyle,
  className,
  style,
  ...rest
}) {
  const isMac = useSyncExternalStore(subscribeToPlatformChanges, detectMac, getServerPlatformSnapshot);
  const parts = keys.split('+').map(key => key.trim().toLowerCase());

  // Screen-reader name: the joined spoken labels (e.g. "Command + K"), since
  // the visual glyphs below are announced meaninglessly by assistive tech.
  const accessibleName = parts.map(key => getKeyLabel(key, isMac)).join(' + ');
  return /*#__PURE__*/_jsx("span", {
    ...rest,
    ref: ref,
    role: "img",
    "aria-label": accessibleName,
    ...mergeProps(themeProps('kbd'), stylex.props(styles.wrapper, xstyle), className, style),
    children: parts.map(key => /*#__PURE__*/_jsx("kbd", {
      "aria-hidden": "true",
      ...{
        className: "x3nfvp2 x6s0dn4 xl56j7k x16asifk x1grt7ep x7a5moj xx3sua9 x17x4s8c xlxy82 x1q0q8m5 xib2hle xv1l7n4 x9ynric x141an7d x1e4wzip x1ltkj2j x87ps6o"
      },
      children: getKeyDisplay(key, isMac)
    }, key))
  });
}
Kbd.displayName = 'Kbd';